/*  MUI  */
import { Box, Button, Typography } from '@mui/material';

/*  Shared  */
import { Transition } from 'application/Shared/Helpers';
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';

/**
 * Component for handling image upload functionality with preview and confirmation modal
 * @component
 * @param {Object} props
 * @param {Function} props.uploadFn - Function to handle the image upload
 * @param {Function} props.removeFn - Function to handle removing the uploaded image
 * @param {boolean} props.brandLogoExists - Whether a logo currently exists
 * @param {string} props.text - Text for the upload button
 * @param {string} props.textDescription - Description text shown in upload modal
 * @param {string} props.textSelect - Text for selecting a new image
 * @param {string} props.textConfirm - Text for confirming image upload
 * @param {string} props.removeText - Text for remove button
 * @param {Object} [props.modalStyle] - Optional styles for the upload modal
 */
class ImageUpload extends Component {
    constructor(props) {
        super(props);
        this.state = {
            openPicture: false,
            photoSelected: false,
            temporaryPhoto: null
        };
        this.fileInput = React.createRef();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.brandLogoExists !== this.props.brandLogoExists) {
            this.setState({ photoSelected: false });
        }
    }

    async handleUpload(event) {
        const { uploadFn } = this.props;
        const { temporaryPhoto } = this.state;

        event.preventDefault();
        uploadFn(btoa(temporaryPhoto));
        this.setState({ openPicture: false });
    }

    async handleImageSelect() {
        if (this.fileInput.current.files[0]) {
            const reader = new FileReader();
            reader.onload = async (e) => {
                this.setState({
                    photoSelected: true,
                    temporaryPhoto: e.target.result
                });
            };
            reader.readAsDataURL(this.fileInput.current.files[0]);
        }
    }

    renderModal = () => {
        const {
            modalStyle,
            t,
            text,
            textConfirm,
            textDescription,
            textSelect
        } = this.props;
        const { openPicture, photoSelected, temporaryPhoto } = this.state;

        return (
            <Modal
                aria-labelledby="form-dialog-title"
                data-testid="imageUploadModal"
                onClose={() => {
                    this.setState({
                        openPicture: false,
                        photoSelected: false,
                        temporaryPhoto: null
                    });
                }}
                open={openPicture}
                style={modalStyle}
                title={text}
                TransitionComponent={Transition}
            >
                <form onSubmit={(e) => this.handleUpload(e)}>
                    <ModalBody>
                        <Typography>{textDescription}</Typography>

                        {!photoSelected && (
                            <Box my="16px" textAlign="center">
                                <Button
                                    data-testid="imageSelectFile"
                                    data-trackid="imageUpload_selectFile"
                                    variant="contained"
                                    color="primary"
                                    component="label"
                                    disabled={photoSelected}
                                >
                                    {textSelect}

                                    <input
                                        data-testid="imageUploadFile"
                                        ref={this.fileInput}
                                        type="file"
                                        accept="image/*"
                                        onChange={() => {
                                            this.handleImageSelect();
                                        }}
                                        style={{ display: 'none' }}
                                    />
                                </Button>
                            </Box>
                        )}

                        {photoSelected && (
                            <Box my="16px" textAlign="center">
                                <img
                                    style={{ maxHeight: 'auto', maxWidth: 400 }}
                                    alt={t('ImageUpload.imageSelected')}
                                    src={temporaryPhoto}
                                />
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            data-testid="imageUploadModalCancelBtn"
                            data-trackid="imageUpload_cancel"
                            onClick={() => {
                                this.setState({
                                    openPicture: false,
                                    photoSelected: false,
                                    temporaryPhoto: null
                                });
                            }}
                            color="primary"
                            variant="outlined"
                        >
                            {t('Common.cancel')}
                        </Button>

                        {photoSelected && (
                            <Button
                                data-testid="imageUploadModalSaveBtn"
                                data-trackid="imageUpload_save"
                                type="submit"
                                variant="contained"
                                color="primary"
                            >
                                {textConfirm}
                            </Button>
                        )}
                    </ModalFooter>
                </form>
            </Modal>
        );
    };

    render() {
        const { text, removeFn, removeText, brandLogoExists } = this.props;
        const { openPicture } = this.state;

        return (
            <Box my="8px">
                <Button
                    data-testid="imageUploadBtn"
                    data-trackid="imageUpload_open"
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => {
                        this.setState({ openPicture: true });
                    }}
                >
                    {text}
                </Button>
                <Button
                    disabled={!brandLogoExists}
                    sx={{ marginLeft: '8px' }}
                    data-testid="imageRemoveBtn"
                    data-trackid="imageUpload_remove"
                    variant="contained"
                    color="secondary"
                    size="small"
                    onClick={removeFn}
                >
                    {removeText}
                </Button>

                {openPicture && this.renderModal()}
            </Box>
        );
    }
}
export default withTranslation()(ImageUpload);
